"use client";

import React, { useState, useRef, useEffect } from "react";
import { useField } from "formik";
import { ImagePlus, X, Image as ImageIcon, AlertCircle } from "lucide-react";

interface ImageUploadFieldProps {
  label: string;
  name: string;
  required?: boolean;
  maxSize?: number; // in MB
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  name,
  required = false,
  maxSize = 10,
}) => {
  const [field, meta, helpers] = useField(name);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isError = meta.touched && meta.error;

  useEffect(() => {
    if (field.value) {
      if (typeof field.value === "string") {
        setPreview(field.value);
      } else if (field.value instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(field.value);
      }
    } else {
      setPreview(null);
    }
  }, [field.value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > maxSize * 1024 * 1024) {
        alert(`File is too large. Max size is ${maxSize}MB`);
        return;
      }
      helpers.setValue(file);
      helpers.setTouched(true);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    helpers.setValue("");
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-1.5 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <label
          htmlFor={name}
          className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {isError && (
          <div className="flex items-center gap-1 text-[10px] text-red-500 font-bold">
            <AlertCircle size={10} />
            <span>{meta.error}</span>
          </div>
        )}
      </div>

      <div
        onClick={handleClick}
        className={`flex-1 min-h-[160px] relative rounded-xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center p-4 gap-3 ${
          isError
            ? "border-red-200 bg-red-50/30"
            : "border-slate-200 hover:border-gold-dim hover:bg-slate-50/50"
        }`}
      >
        {preview ? (
          <div className="relative group w-full h-full flex items-center justify-center">
            <img
              src={preview}
              alt="Preview"
              className="max-h-32 rounded-lg object-contain shadow-sm transition-transform group-hover:scale-[1.02]"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 p-1 bg-white border border-slate-200 rounded-full text-slate-400 hover:text-red-500 hover:border-red-200 shadow-sm transition-all"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <ImagePlus size={24} />
            </div>
            <div className="text-center">
              <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                Click to upload
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                JPG, PNG or WEB up to {maxSize}MB
              </p>
            </div>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        id={name}
        name={name}
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};
